import React, { useContext, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { CircularProgress, makeStyles } from '@material-ui/core';
import { AppContext } from 'context';
import PageContent from 'components/Generic/PageContent';
import { Routes } from 'routes';
import { TableFilterProvider } from 'tableFilterContext';
import { formatFilterAsQueryParams } from 'utils/filters';
import { downloadFile } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import Dialog from 'components/Dialog';
import { DialogWidth, ProductionFilters } from 'types';
import ProductionList from './ProductionList';

const useStyles = makeStyles({
  downloadModalSubtitle: {
    marginTop: 0,
    fontWeight: 600,
    fontSize: 14,
  },
});

function downloadProductions(
  sortingCenterId: string,
  filter: ProductionFilters,
  t: (m: string) => string,
  setModalVisible: (isVisible: boolean) => void,
): void {
  let url = `/sorting_centers/${sortingCenterId}/productions/report`;
  const params = new URLSearchParams({
    ...formatFilterAsQueryParams(filter, 'productionDate'),
    sortingCenter: sortingCenterId,
  });

  if (!!params.toString()) {
    url += '?' + params.toString();
  }
  setModalVisible(true);
  downloadFile(url)
    .then(() => {
      setModalVisible(false);
      notify({
        message: t('productions.export.success'),
        severity: 'success',
      });
    })
    .catch(() => {
      notify({
        message: t('genericError'),
        severity: 'error',
      });
      setModalVisible(false);
    });
}

const ProductionListPage: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const sortingCenterId = sortingCenter?.['@id'].split('/')[3];

  const [downloadModalVisible, setDownloadModalVisible] = useState<boolean>(false);
  const [filter, setFilter] = useState<ProductionFilters | null>({});
  const [isExportDisabled, setExportDisabled] = useState<boolean>(true);

  return (
    <PageContent
      title={t('productions.entityName')}
      actionButtonLink={{ pathname: Routes.ProductionCreate, state: { referer: Routes.Productions } }}
      noPadding
      exportButtonHandler={() => {
        if (!sortingCenterId) return;
        downloadProductions(sortingCenterId, filter ?? {}, t, setDownloadModalVisible);
      }}
      exportButtonDisabled={isExportDisabled}
    >
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={downloadModalVisible}
        width={DialogWidth.SMALL_FORM}
        renderContent={() => (
          <>
            <h1
              style={{
                fontWeight: 600,
                fontSize: 24,
              }}
            >
              {t('productions.export.title')}
            </h1>
            <h2 className={classes.downloadModalSubtitle}>{t('productions.export.subtitle')}</h2>
            <CircularProgress style={{ marginBottom: 10 }} size={49} thickness={6} />
          </>
        )}
      />
      <TableFilterProvider>
        <ProductionList filter={filter} setFilter={setFilter} setExportDisabled={setExportDisabled} />
      </TableFilterProvider>
    </PageContent>
  );
};
export default ProductionListPage;
